import { useState } from "react";

import { DashboardKpiCardHeader } from "@/components/dashboard/dashboard-stats/DashboardKpiCardHeader";
import { DashboardStafferFunnelRatesCard } from "@/components/dashboard/dashboard-stats/trend-and-funnel-charts/DashboardStafferFunnelRatesCard";
import { DashboardStafferTrendChart } from "@/components/dashboard/dashboard-stats/trend-and-funnel-charts/DashboardStafferTrendChart";
import { Card } from "@ui/card";
import { Skeleton } from "@ui/skeleton";

import { DASHBOARD_STAT_CARD_HEIGHT_CLASS } from "@/lib/constants";
import { cn } from "@/lib/utils";
import {
  DashboardKpiOverview,
  DashboardSelectedUser,
  StafferStatKind,
} from "@/types";

// The submit splits and funnel ratios that sit outside the three headline KPIs.
enum OtherMetric {
  SEARCH = "SEARCH",
  SALLY = "SALLY",
  FUNNEL = "FUNNEL",
}

const METRICS = [OtherMetric.SEARCH, OtherMetric.SALLY, OtherMetric.FUNNEL];

export const DashboardIndividualOtherCard = ({
  isPending,
  overview,
  selectedUserId,
}: {
  isPending: boolean;
  overview: DashboardKpiOverview | undefined;
  selectedUserId: DashboardSelectedUser;
}) => {
  /* React Hooks */
  const [metric, setMetric] = useState(OtherMetric.SEARCH);

  /* Helpers */
  const label =
    metric === OtherMetric.SEARCH ? "Search submits" : "Sally submits";
  const statKind =
    metric === OtherMetric.SEARCH
      ? StafferStatKind.SEARCH_SUBMITS
      : StafferStatKind.SOURCING_SUBMITS;

  /* Handlers */
  const handleNextMetric = () => {
    const activeIndex = METRICS.indexOf(metric);
    setMetric(METRICS[(activeIndex + 1) % METRICS.length]);
  };

  /* Render */
  const renderChart = () => {
    if (isPending) return <Skeleton className="min-h-0 flex-1" />;
    return (
      <DashboardStafferTrendChart
        kind={statKind}
        rows={overview ? overview.trend : []}
        valueForRow={(row) =>
          metric === OtherMetric.SEARCH
            ? row.laborupSearchSubmits
            : row.internalSourcingSubmits
        }
        onOpenScreenBreakdown={() => undefined}
      />
    );
  };

  const render = () => {
    if (metric === OtherMetric.FUNNEL) {
      return (
        <DashboardStafferFunnelRatesCard
          eyebrow="Other"
          isPending={isPending}
          onNext={handleNextMetric}
          selectedUserId={selectedUserId}
          title="Funnel ratios"
        />
      );
    }

    return (
      <Card
        className={cn(
          "flex min-h-0 w-full min-w-0 flex-col overflow-hidden px-4 pb-0 pt-3.5 shadow-none",
          DASHBOARD_STAT_CARD_HEIGHT_CLASS,
        )}
      >
        <DashboardKpiCardHeader
          className="mb-2"
          eyebrow="Other"
          onNext={handleNextMetric}
          title={label}
        />
        {renderChart()}
      </Card>
    );
  };

  return render();
};
